import { Injectable } from '@angular/core';
import { LicensePlate, LPRCountryCode, SignupSearchStatusResponse } from '@shared/models';
import { BehaviorSubject, ReplaySubject, Observable, tap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { FeatureFlagService } from '@core/services';
import { SignupSessionPayload } from '@core/auth';

const ONBOARDING_SIGNUP_FORM_STORAGE_KEY = 'onboarding_signup_form';

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  licensePlate: BehaviorSubject<LicensePlate | null> = new BehaviorSubject<LicensePlate | null>(null);
  googleSignupTermsRequiredError: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  onboardingSessionStatus: BehaviorSubject<SignupSearchStatusResponse | null> = new BehaviorSubject<SignupSearchStatusResponse | null>(
    null,
  );

  constructor(
    private http: HttpClient,
    private featureFlagService: FeatureFlagService,
  ) {}

  get publicProductsPageEnabled(): boolean {
    return this.featureFlagService.getFeatureFlag('enable_public_products');
  }

  getOnboardingSessionStatus(licensePlate: LicensePlate): Observable<SignupSearchStatusResponse> {
    return this.http
      .get<SignupSearchStatusResponse>(
        `/api/user/v2/auth/register/search/?license_plate=${licensePlate.license_plate}&license_plate_country=${licensePlate.license_plate_country}`,
      )
      .pipe(
        tap((res) => {
          this.licensePlate.next(licensePlate);
          this.onboardingSessionStatus.next(res);
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  sendOnboardingEmail(licensePlate: LicensePlate, email: string, marketingConsent?: boolean): Observable<any> {
    let request: { email: string; license_plate: string; license_plate_country: LPRCountryCode; marketing_consent?: boolean } = {
      email: email,
      license_plate: licensePlate.license_plate,
      license_plate_country: licensePlate.license_plate_country,
    };

    if (this.featureFlagService.getFeatureFlag('enable_marketing_consent')) {
      request.marketing_consent = marketingConsent;
    }

    return this.http.post(`/api/user/v2/auth/register/email/`, request);
  }

  loadSignupPayloadFromSession(): SignupSessionPayload | null {
    let rawPayload: string | null = null;

    try {
      rawPayload = sessionStorage.getItem(ONBOARDING_SIGNUP_FORM_STORAGE_KEY);
      sessionStorage.removeItem(ONBOARDING_SIGNUP_FORM_STORAGE_KEY);
    } catch {
      this.cleanSignupPayloadFromSession();
      return null;
    }

    if (!rawPayload) {
      return null;
    }

    try {
      return JSON.parse(rawPayload) as SignupSessionPayload;
    } catch {
      return null;
    }
  }

  cleanSignupPayloadFromSession(): void {
    try {
      sessionStorage.removeItem(ONBOARDING_SIGNUP_FORM_STORAGE_KEY);
    } catch {
      return;
    }
  }

  saveSignupPayloadToSession(payload: SignupSessionPayload): boolean {
    try {
      sessionStorage.setItem(ONBOARDING_SIGNUP_FORM_STORAGE_KEY, JSON.stringify(payload));
      return true;
    } catch {
      return false;
    }
  }

}
